// Protected Route Component
import { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { Loading } from '../ui/loading';

// P-01: Timeout en segundos para la inicialización de Firebase
const INIT_TIMEOUT_MS = 15000;

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  // P-02: Solo usar `initialized` para la pantalla de espera, no `loading`.
  // `loading` se activa durante signIn/signUp/logout, lo cual no debería
  // mostrar un spinner en rutas protegidas ya renderizadas.
  const { user, initialized, emailVerified } = useAuthStore();
  const location = useLocation();
  const [timedOut, setTimedOut] = useState(false);

  // P-01: Timeout para evitar spinner indefinido si Firebase no inicializa
  useEffect(() => {
    if (initialized) return;

    const timer = setTimeout(() => {
      setTimedOut(true);
    }, INIT_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [initialized]);

  if (!initialized) {
    if (timedOut) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center space-y-4 max-w-md px-4">
            <p className="text-gray-900 dark:text-gray-100 font-semibold">
              No se pudo conectar con el servicio de autenticación
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Verifica tu conexión a internet e intenta nuevamente.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors"
            >
              Reintentar
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading size="lg" />
      </div>
    );
  }

  if (!user) {
    // I-01: Preservar la URL original para redirigir después del login
    return <Navigate to="/login" replace state={{ returnTo: location.pathname + location.search }} />;
  }

  // [H-T005] Redirigir a verificación de email preservando returnTo
  if (!emailVerified) {
    return <Navigate to="/verify-email" replace state={{ returnTo: location.pathname + location.search }} />;
  }

  return <>{children}</>;
}

